import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { ReceiptLongOutlined } from "@mui/icons-material";

import CustomSnackBar from "../../Components/CustomSnackBar";
import MainText from "../../Components/MainText";
import { invoiceService } from "../../services";

const STATUS_OPTIONS = [
  { value: "", label: "Todos" },
  { value: "DRAFT", label: "Borrador" },
  { value: "READY", label: "Lista" },
  { value: "ISSUED_INTERNAL", label: "Emitida interna" },
  { value: "CANCELLED", label: "Cancelada" },
];

const STATUS_LABELS = {
  DRAFT: "Borrador",
  READY: "Lista",
  ISSUED_INTERNAL: "Emitida interna",
  CANCELLED: "Cancelada",
};

const STATUS_COLORS = {
  DRAFT: "default",
  READY: "info",
  ISSUED_INTERNAL: "success",
  CANCELLED: "default",
};

const initialFilters = {
  search: "",
  status: "",
  date_from: "",
  date_to: "",
};

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  return [];
};

const toMoney = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "Q0.00";
  return `Q${parsed.toFixed(2)}`;
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
};

const extractErrorMessage = (error, fallback = "No se pudo completar la operación.") => {
  const data = error?.response?.data;
  if (!data || typeof data !== "object") return fallback;
  if (typeof data.detail === "string" && data.detail.trim()) return data.detail;
  if (Array.isArray(data.detail) && data.detail[0]) return String(data.detail[0]);
  const firstKey = Object.keys(data)[0];
  const firstValue = data[firstKey];
  if (Array.isArray(firstValue) && firstValue[0]) return String(firstValue[0]);
  if (typeof firstValue === "string" && firstValue.trim()) return firstValue;
  return fallback;
};

function InvoiceDocumentsPage() {
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "info" });

  const params = useMemo(() => {
    const next = {};
    Object.entries(appliedFilters).forEach(([key, value]) => {
      const normalized = String(value || "").trim();
      if (normalized) next[key] = normalized;
    });
    return next;
  }, [appliedFilters]);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const data = await invoiceService.listDocuments(params);
      setRows(toArray(data));
    } catch {
      setRows([]);
      setSnack({ open: true, message: "No se pudieron cargar las facturas internas.", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const updateFilter = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const applyFilters = () => {
    setAppliedFilters(filters);
  };

  const clearFilters = () => {
    setFilters(initialFilters);
    setAppliedFilters(initialFilters);
  };

  const openDetail = async (row) => {
    if (!row?.id) return;
    setDetailOpen(true);
    setDetailLoading(true);
    setDetail(null);
    try {
      const data = await invoiceService.getDocument(row.id);
      setDetail(data);
    } catch (error) {
      setSnack({
        open: true,
        message: extractErrorMessage(error, "No se pudo cargar el detalle de la factura interna."),
        severity: "error",
      });
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <MainText
        title="Facturas internas"
        subtitle="Documentos internos preparados desde ventas pendientes de facturación."
        icon={ReceiptLongOutlined}
      />

      <Card variant="outlined">
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
          <Stack spacing={1.25}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.25}>
              <TextField
                size="small"
                label="Buscar"
                value={filters.search}
                onChange={(event) => updateFilter("search", event.target.value)}
                fullWidth
              />
              <TextField
                select
                size="small"
                label="Estado"
                value={filters.status}
                onChange={(event) => updateFilter("status", event.target.value)}
                sx={{ minWidth: { md: 190 } }}
              >
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value || "all"} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                label="Desde"
                type="date"
                value={filters.date_from}
                onChange={(event) => updateFilter("date_from", event.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: { md: 160 } }}
              />
              <TextField
                size="small"
                label="Hasta"
                type="date"
                value={filters.date_to}
                onChange={(event) => updateFilter("date_to", event.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: { md: 160 } }}
              />
            </Stack>

            <Stack direction="row" justifyContent="flex-end" spacing={1}>
              <Button variant="outlined" onClick={clearFilters} disabled={loading}>
                Limpiar
              </Button>
              <Button variant="contained" onClick={applyFilters} disabled={loading}>
                Filtrar
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
          <Stack spacing={1.25}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Documentos
            </Typography>
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Venta</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>NIT</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell align="right">Acción</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                          {loading ? "Cargando facturas internas..." : "No hay facturas internas."}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => {
                      const status = String(row.status || "").toUpperCase();
                      return (
                        <TableRow key={row.id} hover>
                          <TableCell>{formatDateTime(row.created_at)}</TableCell>
                          <TableCell>{row.sale_number || row.sale_id || "-"}</TableCell>
                          <TableCell>{row.customer_name || "Consumidor Final"}</TableCell>
                          <TableCell>{row.customer_nit || "CF"}</TableCell>
                          <TableCell align="right">{toMoney(row.total)}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={STATUS_LABELS[status] || row.status || "-"}
                              color={STATUS_COLORS[status] || "default"}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Button size="small" variant="outlined" onClick={() => openDetail(row)}>
                              Ver
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Factura interna</DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Typography variant="body2" color="text.secondary">
              Cargando detalle...
            </Typography>
          ) : detail ? (
            <Stack spacing={1.5}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1.25}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary">Venta</Typography>
                  <Typography variant="subtitle2">{detail.sale_number || detail.sale_id || "-"}</Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary">Cliente</Typography>
                  <Typography variant="subtitle2">{detail.customer_name || "Consumidor Final"}</Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary">NIT</Typography>
                  <Typography variant="subtitle2">{detail.customer_nit || "CF"}</Typography>
                </Box>
              </Stack>
              <Box>
                <Typography variant="body2" color="text.secondary">Dirección</Typography>
                <Typography variant="body2">{detail.customer_address || "-"}</Typography>
              </Box>

              <Divider />

              <Box sx={{ overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Producto</TableCell>
                      <TableCell align="right">Cantidad</TableCell>
                      <TableCell align="right">Precio</TableCell>
                      <TableCell align="right">Descuento</TableCell>
                      <TableCell align="right">Subtotal</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {toArray(detail.lines).map((line) => (
                      <TableRow key={line.id}>
                        <TableCell>{line.product_name || "-"}</TableCell>
                        <TableCell align="right">{Number(line.quantity || 0).toFixed(3)}</TableCell>
                        <TableCell align="right">{toMoney(line.unit_price)}</TableCell>
                        <TableCell align="right">{toMoney(line.discount_amount)}</TableCell>
                        <TableCell align="right">{toMoney(line.subtotal)}</TableCell>
                        <TableCell align="right">{toMoney(line.total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>

              <Divider />

              <Stack spacing={0.5} sx={{ alignSelf: { xs: "stretch", md: "flex-end" }, minWidth: { md: 260 } }}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Subtotal</Typography>
                  <Typography variant="body2">{toMoney(detail.subtotal)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Impuesto</Typography>
                  <Typography variant="body2">{toMoney(detail.tax_amount)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Total</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{toMoney(detail.total)}</Typography>
                </Stack>
              </Stack>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <CustomSnackBar
        open={snack.open}
        setOpen={(open) => setSnack((prev) => ({ ...prev, open }))}
        message={snack.message}
        severity={snack.severity}
      />
    </Box>
  );
}

export default InvoiceDocumentsPage;
